import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { historyListSchema, personSchema, type HistoryEntry } from '@forecastify/shared';

/** Somebody the *Ki* filter can name — active or not. */
export interface HistoryActor {
  readonly id: string;
  readonly displayName: string;
}
import { useSession } from '../../session';
import { failureOf, type ScreenFailure } from '../screen-state';
import { parseList, totalOf } from '../permissions-hook';
import { useListQuery, type ListQuery } from '../use-list-query';
import { requestAll } from '../read-all';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ux-pass-2 · one history, from any address that answers `historyListSchema`.│
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `GET /audit` for the workspace, `GET /history/:type/:id` for one record — the same envelope,
 * the same query (`historyQuerySchema`), so the same hook. It holds the search, the filters and the
 * page through `useListQuery`, which is what makes *a filter resets the page* impossible to forget
 * (`state-rules.md` §6).
 *
 * ⚠️⚠️ **`q`, NOT `search`, AND NO `sort`.** `useListQuery.params` speaks the other lists'
 * dialect; the history contract names its search `q` and has one order — newest first — so this
 * builds its own query string from the same state rather than sending two parameters nothing reads.
 *
 * ⚠️ **The date range is ONE filter, `from..to`** — `list-toolbar.tsx`'s own shape — split here,
 * exactly once, where it becomes the contract's `from` and `to`.
 *
 * ⚠️ **A late answer to an older question is dropped.** Typing in the search box asks a question
 * per keystroke; without the ticket the slowest response, not the newest, decides the rows.
 */

export const HISTORY_FILTERS = { kind: '', actorMemberId: '', entityType: '', at: '' } as const;

export interface HistoryState {
  readonly entries: readonly HistoryEntry[];
  /** The first read only — `use-first-load.ts`. A refetch keeps the rows it has on screen. */
  readonly loading: boolean;
  readonly failure: ScreenFailure | null;
  readonly query: ListQuery;
  readonly reload: () => Promise<void>;
}

/** Rows per page in every history — the audit log and each record's. */
export const HISTORY_PAGE_SIZE = 25;

/** The contract's query string out of the list state. Exported for the test. */
export function historyParams(
  search: string,
  filters: Readonly<Record<string, string>>,
  offset: number,
  limit?: number,
): URLSearchParams {
  const params = new URLSearchParams();
  if (limit !== undefined) params.set('limit', String(limit));
  if (search.trim() !== '') params.set('q', search.trim());
  for (const key of ['kind', 'actorMemberId', 'entityType'] as const) {
    const value = filters[key] ?? '';
    if (value !== '') params.set(key, value);
  }
  const [from = '', to = ''] = (filters.at ?? '').split('..');
  if (from !== '') params.set('from', from);
  if (to !== '') params.set('to', to);
  if (offset > 0) params.set('offset', String(offset));
  return params;
}

/**
 * @param path `/audit` or `/history/<type>/<id>`. `null` reads nothing — a host whose record has
 * not arrived yet holds the hook mounted anyway.
 */
export function useHistory(path: string | null): HistoryState {
  const session = useSession();
  /* ⚠️ 25 a page, every history in the app (14 Oct 2026): a log is read a screenful at a time. */
  /* 3 Nov 2026 · the workspace audit log remembers its filters (`list-memory.ts`); an entity's own
     history is a question about that one record and starts clean. */
  const query = useListQuery(
    HISTORY_FILTERS,
    [],
    HISTORY_PAGE_SIZE,
    path === '/audit' ? { persistKey: 'audit' } : {},
  );
  const { search, filters, pages } = query;
  const { settle, offset } = pages;

  const [entries, setEntries] = useState<readonly HistoryEntry[]>([]);
  const [failure, setFailure] = useState<ScreenFailure | null>(null);
  const [loading, setLoading] = useState(true);
  const ticket = useRef(0);

  const text = useMemo(
    () => historyParams(search, filters, offset, HISTORY_PAGE_SIZE).toString(),
    [search, filters, offset],
  );

  const reload = useCallback(async (): Promise<void> => {
    if (path === null) return;
    if (session.workspaceId === null) {
      setFailure({ kind: 'no-workspace' });
      setLoading(false);
      return;
    }

    const mine = ++ticket.current;
    const current = (): boolean => mine === ticket.current;
    const response = await session
      .request(`${path}${text === '' ? '' : `?${text}`}`)
      .catch(() => null);
    if (!current()) return;

    if (response === null) {
      setFailure({ kind: 'error', code: 'unavailable' });
    } else if (!response.ok) {
      const refused = await failureOf(response);
      if (!current()) return;
      setFailure(refused);
    } else {
      const body: unknown = await response.json().catch(() => null);
      if (!current()) return;
      const parsed = historyListSchema.safeParse(body);
      if (parsed.success) {
        setFailure(null);
        setEntries(parsed.data.data);
        settle(totalOf(body));
      } else {
        setFailure({ kind: 'error', code: 'validation_failed' });
      }
    }
    setLoading(false);
  }, [session, path, text, settle]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { entries, loading, failure, query, reload };
}

/**
 * The workspace's members, for the *Ki* filter.
 *
 * ⚠️ **From `/people`, not from the rows on screen** — the project time tab's argument: a filter
 * built from one page can only offer the people who appear on it, so filtering by anybody else
 * would be unreachable rather than empty. Non-fatal: without `people.view` the filter is simply
 * not offered.
 *
 * ⚠️ **`/people`, not `/members` (3 Nov 2026)** — `/members` lists ACTIVE people only, and a filter
 * over history has to reach somebody deactivated since: their changes are still in the log.
 */
export function useHistoryMembers(): readonly HistoryActor[] {
  const session = useSession();
  const [members, setMembers] = useState<readonly HistoryActor[]>([]);

  useEffect(() => {
    const alive = { current: true };
    void (async () => {
      if (session.workspaceId === null) return;
      const response = await requestAll(session, '/people').catch(() => null);
      if (response?.ok !== true) return;
      const parsed = parseList(personSchema, await response.json().catch(() => null));
      if (parsed !== null && alive.current) setMembers(parsed);
    })();
    return () => {
      alive.current = false;
    };
  }, [session]);

  return members;
}
